import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { UserRole } from './core/models/user-role.enum';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'student',
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.Student] },
    children: [
      /*
       * Immersive practice screens render full-page, outside the shell — the
       * design gives each its own close affordance. Declared before the shell
       * route so they win the match.
       */
      {
        path: 'lexicons/:lexiconId/flashcards',
        title: 'Flashcards · Polilo',
        loadComponent: () =>
          import('./features/student/lexicons/flashcards/flashcards.component').then(
            (m) => m.FlashcardsComponent,
          ),
      },
      {
        path: 'assignments/quiz/:quizId',
        title: 'Quiz · Polilo',
        loadComponent: () =>
          import('./features/student/assignments/quiz/quiz.component').then((m) => m.QuizComponent),
      },
      {
        path: 'play/:sceneId',
        title: 'Play · Polilo',
        loadComponent: () =>
          import('./features/game/player/game-play-page.component').then((m) => m.GamePlayPageComponent),
      },
      {
        path: 'loader',
        loadComponent: () =>
          import('./layouts/blank-layout/blank-layout.component').then((m) => m.BlankLayoutComponent),
        children: [
          {
            path: 'lesson/:id',
            title: 'Lesson · Polilo',
            data: { kind: 'lesson' },
            loadComponent: () =>
              import('./shared/components/question-loader').then((m) => m.QuestionLoaderComponent),
          },
          {
            path: 'quiz/:id',
            title: 'Quiz · Polilo',
            data: { kind: 'quiz' },
            loadComponent: () =>
              import('./shared/components/question-loader').then((m) => m.QuestionLoaderComponent),
          },
          {
            path: 'assignment/:id',
            title: 'Assignment · Polilo',
            data: { kind: 'assignment' },
            loadComponent: () =>
              import('./shared/components/question-loader').then((m) => m.QuestionLoaderComponent),
          },
          {
            path: '',
            title: 'Question loader · Polilo',
            loadComponent: () =>
              import('./shared/components/question-loader').then((m) => m.QuestionLoaderComponent),
          },
        ],
      },
      {
        path: '',
        loadComponent: () =>
          import('./layouts/student-layout/student-layout.component').then((m) => m.StudentLayoutComponent),
        loadChildren: () => import('./features/student/student.routes').then((m) => m.STUDENT_ROUTES),
      },
    ],
  },
  {
    path: 'teacher',
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.Teacher] },
    children: [
      {
        path: 'play/:sceneId',
        title: 'Play · Polilo',
        loadComponent: () =>
          import('./features/game/player/game-play-page.component').then((m) => m.GamePlayPageComponent),
      },
      {
        path: '',
        loadComponent: () =>
          import('./layouts/teacher-layout/teacher-layout.component').then(
            (m) => m.TeacherLayoutComponent,
          ),
        loadChildren: () => import('./features/teacher/teacher.routes').then((m) => m.TEACHER_ROUTES),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.Admin] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./layouts/admin-layout/admin-layout.component').then((m) => m.AdminLayoutComponent),
        loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
    ],
  },
  /**
   * Iframe embed — loads player mode and delegates exit/save/load/reset
   * to the parent page via `window.poliloGamePlayer`.
   */
  {
    path: 'embed/play',
    title: 'Play · Polilo',
    loadComponent: () =>
      import('./features/game/player/game-play-embed-page.component').then(
        (m) => m.GamePlayEmbedPageComponent,
      ),
  },
  { path: '', pathMatch: 'full', redirectTo: 'student/dashboard' },
  { path: '**', redirectTo: 'student/dashboard' },
];
